import { STORAGE_KEYS, getRedirectUri } from '../config.ts';
import type { StoredTokens } from './tokens.ts';

const ACCOUNTS_BASE = 'https://accounts.spotify.com';

/**
 * Benoetigte Berechtigungen.
 * Bewusst minimal gehalten – jede zusaetzliche Scope muss begruendet sein.
 */
export const SCOPES = [
  'user-read-private', // liefert `product` – noetig fuer die Premium-Pruefung
  'user-read-playback-state', // Wiedergabestatus + Geraete lesen
  'user-modify-playback-state', // Abspielen, Pause, Seek, Lautstaerke
  'user-read-currently-playing', // aktuell laufender Titel
  'user-library-read', // gespeicherte Alben/Shows fuer Katalog-Vorschlaege
  'playlist-read-private', // eigene Playlists als Katalogquelle
] as const;

/** Prueft, ob ein gespeicherter Scope-String alle benoetigten Rechte abdeckt. */
export function hasAllScopes(grantedScope: string): boolean {
  const granted = new Set(grantedScope.split(' ').filter(Boolean));
  return SCOPES.every((scope) => granted.has(scope));
}

const VERIFIER_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

function randomString(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  let out = '';
  for (const byte of bytes) {
    out += VERIFIER_ALPHABET[byte % VERIFIER_ALPHABET.length];
  }
  return out;
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function createChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(digest);
}

/**
 * Startet den Authorization Code Flow mit PKCE.
 * Verifier und State landen im localStorage, damit sie den Redirect ueberleben
 * (in iOS-Standalone-PWAs ist sessionStorage nicht zuverlaessig).
 */
export async function beginLogin(clientId: string): Promise<void> {
  const verifier = randomString(96);
  const state = randomString(24);

  localStorage.setItem(STORAGE_KEYS.pkceVerifier, verifier);
  localStorage.setItem(STORAGE_KEYS.pkceState, state);

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: getRedirectUri(),
    code_challenge_method: 'S256',
    code_challenge: await createChallenge(verifier),
    state,
    scope: SCOPES.join(' '),
  });

  window.location.assign(`${ACCOUNTS_BASE}/authorize?${params.toString()}`);
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope?: string;
}

async function requestTokens(body: URLSearchParams): Promise<TokenResponse> {
  const response = await fetch(`${ACCOUNTS_BASE}/api/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const payload = (await response.json().catch(() => null)) as
    | (TokenResponse & { error?: string; error_description?: string })
    | null;

  if (!response.ok || !payload?.access_token) {
    const reason = payload?.error_description ?? payload?.error ?? `HTTP ${response.status}`;
    throw new Error(`Spotify-Token-Anfrage fehlgeschlagen: ${reason}`);
  }

  return payload;
}

function toStoredTokens(payload: TokenResponse, previousRefreshToken?: string): StoredTokens {
  const refreshToken = payload.refresh_token ?? previousRefreshToken;
  if (!refreshToken) {
    throw new Error('Spotify hat kein Refresh-Token geliefert.');
  }
  return {
    accessToken: payload.access_token,
    refreshToken,
    expiresAt: Date.now() + payload.expires_in * 1000,
    scope: payload.scope ?? '',
  };
}

/**
 * Prueft, ob die aktuelle URL einen Auth-Callback enthaelt, und tauscht den
 * Code gegen Tokens. Gibt `null` zurueck, wenn kein Callback vorliegt.
 * Raeumt die URL anschliessend auf, damit der Code nicht im Verlauf landet.
 */
export async function completeLoginFromUrl(clientId: string): Promise<StoredTokens | null> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (!code && !error) return null;

  const expectedState = localStorage.getItem(STORAGE_KEYS.pkceState);
  const verifier = localStorage.getItem(STORAGE_KEYS.pkceVerifier);
  localStorage.removeItem(STORAGE_KEYS.pkceState);
  localStorage.removeItem(STORAGE_KEYS.pkceVerifier);

  // URL bereinigen, egal wie der Flow ausgeht.
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');
  window.history.replaceState({}, '', url.toString());

  if (error) {
    throw new Error(`Spotify-Anmeldung abgebrochen: ${error}`);
  }
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Sicherheitsprüfung fehlgeschlagen (state stimmt nicht überein).');
  }
  if (!verifier) {
    throw new Error('PKCE-Verifier fehlt – bitte Anmeldung erneut starten.');
  }

  const payload = await requestTokens(
    new URLSearchParams({
      grant_type: 'authorization_code',
      code: code!,
      redirect_uri: getRedirectUri(),
      client_id: clientId,
      code_verifier: verifier,
    }),
  );

  return toStoredTokens(payload);
}

/**
 * Erneuert das Access-Token. Wichtig: Spotify rotiert bei PKCE das
 * Refresh-Token – das neue muss gespeichert werden, sonst bricht die
 * Anmeldung beim naechsten Refresh ab.
 */
export async function refreshTokens(
  clientId: string,
  refreshToken: string,
): Promise<StoredTokens> {
  const payload = await requestTokens(
    new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    }),
  );

  return toStoredTokens(payload, refreshToken);
}
